import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  CardContent,
  CardDescription,
  CardFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InfiniteScroll,
  Input,
  LoadingSpinner,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import React, { useCallback, useMemo, useState } from 'react';
import { DetectorActionsMenu } from './DetectorActionsMenu';
import {
  useDisableGroundingConfiguration,
  useEditEventdetector,
  useFetchAllEventdetectorDefinitions,
  useFetchAllGroundingConfigurationDefinitions,
  useShapeFetch,
} from '@ais-ui/api';
import { cn } from '@ais-ui/utils';

import { detectorTypeToIcon } from './detectorToIcon';
import { DeleteDetector } from './Mutations/DeleteDetector';
import { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { EditDetectorDialog } from './Mutations/EditDetector';
import { severityToIcon } from '../severityToIcon';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  PiCircleFill,
  PiDotsThreeVertical,
  PiEye,
  PiEyeSlash,
} from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { shapeToVectorLayer, useLayers } from '@ais-ui/react-openlayers';
import { NoItemsEmptyStateList } from '../../../components/EmptyList';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import { GroundingConfigurationSchema } from '@ais-ui/api-spec/grounding';
import { EditGroundingDetectorDialog } from './Grounding/EditGroundingDetector';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { useAuth } from '@ais-ui/auth';
import { useDetectorFilters } from './useDetectorFilters';
import { DetectorSort } from './DetectorSort';
import { DetectorFilter } from './DetectorFilter';

interface DetectorItemProps {
  detector: EventdetectorDefinition;
}

interface GroundingDetectorItemProps {
  detector: GroundingConfigurationSchema;
}

interface EnableDetectorButtonProps {
  detector: EventdetectorDefinition | GroundingConfigurationSchema;
}

const EnableDetectorButton = ({ detector }: EnableDetectorButtonProps) => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const [isApplied, setIsApplied] = useState<boolean>(
    detector.enabled as boolean
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate } = useEditEventdetector();
  const { mutate: groundingMutate } = useDisableGroundingConfiguration();

  const onApply = () => {
    setIsSubmitting(true);
    if (!isApplied) {
      setIsApplied(true);
      detector.enabled = true;
    } else {
      setIsApplied(false);
      detector.enabled = false;
    }

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'edit',
      'detector',
      setIsSubmitting
    );
    if (detector.type === 'grounding') {
      groundingMutate(
        {
          id: detector.id as number,
          disable: !detector.enabled,
        },
        callBacks
      );
    } else {
      mutate(
        {
          id: detector.id as string,
          eventdetectorDefinition: detector as EventdetectorDefinition,
        },
        callBacks
      );
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="smIcon"
          variant="ghost"
          onClick={onApply}
          disabled={isSubmitting}
        >
          {isApplied ? (
            <PiCircleFill className="h-4 w-4 text-green-500"></PiCircleFill>
          ) : (
            <PiCircleFill className="h-4 w-4 text-red-500"></PiCircleFill>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isApplied ? 'Disable event detector' : 'Enable event detector'}
      </TooltipContent>
    </Tooltip>
  );
};

interface ShowDetectorButtonProps {
  detector: EventdetectorDefinition | GroundingConfigurationSchema;
}

const ShowDetectorButton = ({ detector }: ShowDetectorButtonProps) => {
  const { messages } = useLocalization();
  const [isApplied, setIsApplied] = useState<boolean>(false);
  const { toast } = useToast();
  const { addLayer, doesLayerExist, zoomToLayer, removeLayerById } =
    useLayers();
  const shapeFetch = useShapeFetch();
  const onApply = useCallback(async () => {
    if (doesLayerExist(detector.id as string)) {
      removeLayerById(detector.id as string);
      setIsApplied(false);
      return;
    }
    let layer;
    if (detector.shape && !detector.geometry) {
      const shape = await shapeFetch(detector.shape);
      if (!shape) {
        toast({
          title: 'Error',
          description:
            messages.eventdetectorsDetectorListDetectorbuttonErrorMessages,
          variant: 'destructive',
        });
        return;
      }
      layer = shapeToVectorLayer(shape);
    } else {
      layer = shapeToVectorLayer(detector.geometry);
    }
    addLayer(
      layer,
      detector.name as string,
      true,
      true,
      true,
      {},
      detector.id as string
    );
    zoomToLayer(layer);
    setIsApplied(true);
  }, [
    addLayer,
    detector,
    doesLayerExist,
    removeLayerById,
    shapeFetch,
    zoomToLayer,
  ]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button size="smIcon" variant="ghost" onClick={onApply}>
          {isApplied ? (
            <PiEyeSlash className="h-4 w-4" />
          ) : (
            <PiEye className="h-4 w-4"></PiEye>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isApplied ? 'Hide Detector' : 'Show Detector'}
      </TooltipContent>
    </Tooltip>
  );
};

const DetectorDropDownMenu = ({
  detector,
  onDelete,
  onEdit,
  className,
}: {
  detector: EventdetectorDefinition | GroundingConfigurationSchema;
  onDelete: () => void;
  onEdit: (event: React.FormEvent) => void;
  className?: string;
}) => {
  const { messages } = useLocalization();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button
          id={`${detector.name}-dropdown-button`}
          size="icon"
          variant="ghost"
          className="h-8 w-8"
        >
          <PiDotsThreeVertical className="h-6 w-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEdit}>
          {messages.genericEdit}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete}>
          {messages.genericDelete}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const DetectorItem = ({ detector }: DetectorItemProps) => {
  const { messages } = useLocalization();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  const onEdit = () => {
    setShowEditDialog(true);
  };

  const DetectorIcon = useMemo(
    () => detectorTypeToIcon(detector.type),
    [detector]
  );

  const Icon = severityToIcon(detector.severity);

  return (
    <AccordionItem
      key={detector.id}
      value={detector.id as string}
      className={cn('pr-4 pl-2')}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className="flex space-x-1 items-center">
            <ShowDetectorButton detector={detector}></ShowDetectorButton>
            <EnableDetectorButton detector={detector}></EnableDetectorButton>
            <Separator orientation="vertical" className="h-6 bg-gray-300" />
          </div>

          <AccordionTrigger className="w-[205px]" hideChevron={true}>
            <div className="flex ml-4 space-x-2 items-center">
              <DetectorIcon
                className={cn(
                  'h-4 w-4',
                  detector.enabled && 'text-green-500',
                  !detector.enabled && 'text-red-500'
                )}
              />
              <div className="ml-4 space-y-1">
                <p className="text-sm text-left font-medium leading-none group-hover:underline">
                  {detector.name}
                </p>
              </div>
            </div>
          </AccordionTrigger>
        </div>
        <DetectorDropDownMenu
          detector={detector}
          onDelete={onDelete}
          onEdit={onEdit}
        ></DetectorDropDownMenu>
      </div>

      <AccordionContent>
        <div className="space-y-4">
          <div className="flex px-4 space-x-2">
            <p className="text-muted-foreground">
              {messages.eventdetectorsItemEnabledLabel}
            </p>
            <p>
              {detector.enabled
                ? messages.eventdetectorsItemEnabledTrueLabel
                : messages.eventdetectorsItemEnabledFalseLabel}
            </p>
          </div>
          <div className="flex px-4 space-x-2">
            <p className="text-muted-foreground">
              {messages.eventdetectorsItemSeverityLabel}
            </p>
            <div className="flex space-x-2">
              <p>{detector.severity}</p>
              <Icon
                className={cn(
                  'h-4 w-4',
                  detector.severity === 'note' && 'text-blue-500',
                  detector.severity === 'warning' && 'text-yellow-500',
                  detector.severity === 'alert' && 'text-red-500'
                )}
              ></Icon>
            </div>
          </div>
          <div className="flex px-4 space-x-2">
            <p className="text-muted-foreground">
              {messages.eventdetectorsItemOwnerLabel}
            </p>
            <p>{detector.owner}</p>
          </div>
          <div className="flex px-4 space-x-2">
            <p className="text-muted-foreground">
              {messages.eventdetectorsItemSharedWithLabel}
            </p>
            <p>
              {detector?.acl && detector?.acl.length > 0
                ? detector?.acl.map((groupName: string) => groupName).join(', ')
                : messages.eventdetectorsItemNotSharedValue}
            </p>
          </div>
        </div>
      </AccordionContent>
      {showDeleteDialog && (
        <DeleteDetector
          detector={detector}
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
        />
      )}
      {showEditDialog && (
        <EditDetectorDialog
          detector={detector}
          showDialog={showEditDialog}
          setShowDialog={setShowEditDialog}
        />
      )}
    </AccordionItem>
  );
};

const GroundingDetectorItem = ({ detector }: GroundingDetectorItemProps) => {
  const { messages } = useLocalization();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  const onEdit = () => {
    setShowEditDialog(true);
  };

  const DetectorIcon = useMemo(() => detectorTypeToIcon('grounding'), []);

  const Icon = severityToIcon('note');

  return (
    <AccordionItem
      key={detector.uuid}
      value={detector.uuid as string}
      className={cn('pr-4 pl-2')}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className="flex space-x-1 items-center">
            <ShowDetectorButton detector={detector}></ShowDetectorButton>
            <EnableDetectorButton detector={detector}></EnableDetectorButton>
            <Separator orientation="vertical" className="h-6 bg-gray-300" />
          </div>

          <AccordionTrigger className="w-[205px]" hideChevron={true}>
            <div className="flex ml-4 space-x-2 items-center">
              <DetectorIcon
                className={cn(
                  'h-4 w-4',
                  detector.enabled && 'text-green-500',
                  !detector.enabled && 'text-red-500'
                )}
              />
              <div className="ml-4 space-y-1">
                <p className="text-sm text-left font-medium leading-none group-hover:underline">
                  {detector.name}
                </p>
              </div>
            </div>
          </AccordionTrigger>
        </div>
        <DetectorDropDownMenu
          detector={detector}
          onDelete={onDelete}
          onEdit={onEdit}
        ></DetectorDropDownMenu>
      </div>

      <AccordionContent>
        <div className="space-y-4">
          <div className="flex px-4 space-x-2">
            <p className="text-muted-foreground">
              {messages.eventdetectorsItemEnabledLabel}
            </p>
            <p>
              {detector.enabled
                ? messages.eventdetectorsItemEnabledTrueLabel
                : messages.eventdetectorsItemEnabledFalseLabel}
            </p>
          </div>
          <div className="flex px-4 space-x-2">
            <p className="text-muted-foreground">
              {messages.eventdetectorsItemSeverityLabel}
            </p>
            <div className="flex space-x-2">
              <p>{detector.severity}</p>
              <Icon
                className={cn(
                  'h-4 w-4',
                  detector.severity === 'note' && 'text-blue-500',
                  detector.severity === 'warning' && 'text-yellow-500',
                  detector.severity === 'alert' && 'text-red-500'
                )}
              ></Icon>
            </div>
          </div>
          <div className="flex px-4 space-x-2">
            <p className="text-muted-foreground">
              {messages.eventdetectorsItemSharedWithLabel}
            </p>
            <p>
              {detector?.acl && detector?.acl.length > 0
                ? detector?.acl.map((groupName: string) => groupName).join(', ')
                : messages.eventdetectorsItemNotSharedValue}
            </p>
          </div>
        </div>
      </AccordionContent>
      {showDeleteDialog && (
        <DeleteDetector
          detector={detector}
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
        />
      )}
      {showEditDialog && (
        <EditGroundingDetectorDialog
          detector={detector}
          showDialog={showEditDialog}
          setShowDialog={setShowEditDialog}
        />
      )}
    </AccordionItem>
  );
};

interface DetectorArea {
  shape: GeoJson;
  zoom: boolean;
  show: boolean;
}

export const DetectorList = () => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');
  const permissions = usePermissions();
  const { credentials } = useAuth();
  const { data: detectors, isLoading } = useFetchAllEventdetectorDefinitions();

  const { data: groundingDetectors, isLoading: groundingIsLoading } =
    useFetchAllGroundingConfigurationDefinitions(
      permissions.grounding && !!credentials
    );

  const detectorsArray = detectors?.records || [];
  const groundingDetectorsArray = groundingDetectors?.items || [];

  const {
    filteredAndSortedDetectors,
    detectorFilters,
    setDetectorFilters,
    sortConfig,
    setSortConfig,
  } = useDetectorFilters(detectorsArray, groundingDetectorsArray, search);

  return (
    <>
      {/* Controls section - outside CardContent to avoid overflow-hidden */}
      <div className="px-6 pb-4">
        <CardDescription className="mb-4">
          {messages.eventsDetectorListViewAndCreateDetectorsPlaceholder}
        </CardDescription>
        <div className="flex gap-2">
          <Input
            id="search"
            placeholder={messages.eventdetectorsListFindDetector}
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <DetectorSort sortConfig={sortConfig} setSortConfig={setSortConfig} />
          <DetectorFilter
            detectorFilters={detectorFilters}
            setDetectorFilters={setDetectorFilters}
            detectors={detectorsArray}
            groundingDetectors={groundingDetectorsArray}
          />
        </div>
      </div>

      {/* Content section */}
      <CardContent className="flex-1 flex flex-col overflow-hidden pt-0">
        {!isLoading &&
          filteredAndSortedDetectors.length === 0 &&
          search === '' && (
            <NoItemsEmptyStateList
              entity="detector"
              srcImage="empty_list_bro.png"
            >
              <DetectorActionsMenu
                buttonText="Create detector"
                className="w-full h-10"
              />
            </NoItemsEmptyStateList>
          )}

        {isLoading && <LoadingSpinner />}

        {!isLoading &&
          filteredAndSortedDetectors.length === 0 &&
          search !== '' && (
            <div className="flex flex-col items-center justify-center flex-1 text-center">
              <p className="text-muted-foreground">
                No detectors found matching "{search}"
              </p>
            </div>
          )}

        {!isLoading && filteredAndSortedDetectors.length > 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <InfiniteScroll
              items={filteredAndSortedDetectors}
              limit={30}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              renderItem={(detector) => {
                // Check if it's a grounding detector by looking for grounding-specific fields
                const isGroundingDetector =
                  'uuid' in detector && 'createdAt' in detector;

                if (isGroundingDetector) {
                  return (
                    <GroundingDetectorItem
                      key={
                        (detector as GroundingConfigurationSchema).uuid ||
                        detector.id
                      }
                      detector={detector as GroundingConfigurationSchema}
                    />
                  );
                } else {
                  return (
                    <DetectorItem
                      key={detector.id}
                      detector={detector as EventdetectorDefinition}
                    />
                  );
                }
              }}
            />
          </Accordion>
        )}
      </CardContent>

      <CardFooter className="flex flex-grow-0 flex-col items-end min-h-52">
        {filteredAndSortedDetectors.length !== 0 && (
          <DetectorActionsMenu buttonText="Create" />
        )}
      </CardFooter>
    </>
  );
};
